import { Card, Tabs, Table, Button, Space, Modal, Form, Input, InputNumber, Switch, Select, Popconfirm, message, Tag } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, CarOutlined } from '@ant-design/icons';
import { useState, useEffect } from 'react';
import { apiClient } from '../../lib/api-client';
import { t } from '../../i18n';

// ============ INTERFACES ============

interface CarBrand {
  id: number;
  name: string;
  isActive: boolean;
  sortOrder: number;
  _count?: { models: number };
  models?: CarModel[];
}

interface CarModel {
  id: number;
  brandId: number;
  name: string;
  isActive: boolean;
  sortOrder: number;
  brand?: { id: number; name: string };
}

// ============ BRANDS TAB ============

const BrandsTab = () => {
  const [brands, setBrands] = useState<CarBrand[]>([]);
  const [loading, setLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingRecord, setEditingRecord] = useState<CarBrand | null>(null);
  const [saving, setSaving] = useState(false); 
  const [form] = Form.useForm();

  const fetchBrands = async () => {
    setLoading(true);
    try {
      const response = await apiClient.get('/admin/car-brands');
      setBrands(Array.isArray(response.data) ? response.data : response.data?.data || []);
    } catch {
      message.error('Brendlarni yuklashda xatolik');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchBrands(); }, []);

  const handleCreate = () => {
    setEditingRecord(null);
    form.resetFields();
    form.setFieldsValue({ isActive: true, sortOrder: 0 });
    setIsModalOpen(true);
  };

  const handleEdit = (record: CarBrand) => {
    setEditingRecord(record);
    form.resetFields();
    setTimeout(() => form.setFieldsValue(record), 0);
    setIsModalOpen(true);
  };

  const handleDelete = async (id: number) => {
    try {
      await apiClient.delete(`/admin/car-brands/${id}`);
      message.success('Brend o\'chirildi');
      fetchBrands();
    } catch {
      message.error(t('common.error'));
    }
  };

  const handleSubmit = async (values: any) => {
    setSaving(true);
    try {
      if (editingRecord) {
        await apiClient.put(`/admin/car-brands/${editingRecord.id}`, values);
        message.success('Brend yangilandi');
      } else {
        await apiClient.post('/admin/car-brands', values);
        message.success('Brend yaratildi');
      }
      setIsModalOpen(false);
      fetchBrands();
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Xatolik yuz berdi');
    } finally {
      setSaving(false);
    }
  };

  const columns = [
    { title: 'ID', dataIndex: 'id', key: 'id', width: 60 },
    { title: t('common.name'), dataIndex: 'name', key: 'name' },
    {
      title: t('carBrands.modelsTab'),
      key: 'models',
      render: (_: unknown, record: CarBrand) => <Tag color="blue">{record._count?.models || 0}</Tag>,
    },
    {
      title: t('carBrands.sortOrder'),
      dataIndex: 'sortOrder',
      key: 'sortOrder',
      width: 80,
    },
    {
      title: t('common.status'),
      key: 'isActive',
      width: 100,
      render: (_: unknown, record: CarBrand) => (
        <Tag color={record.isActive ? 'green' : 'default'}>
          {record.isActive ? t('common.active') : t('common.inactive')}
        </Tag>
      ),
    },
    {
      title: t('common.actions'),
      key: 'actions',
      width: 100,
      render: (_: unknown, record: CarBrand) => (
        <Space>
          <Button type="text" icon={<EditOutlined />} onClick={() => handleEdit(record)} />
          <Popconfirm title="O'chirishni tasdiqlaysizmi?" onConfirm={() => handleDelete(record.id)}>
            <Button type="text" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <>
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'flex-end' }}>
        <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate}>
          Yangi brend
        </Button>
      </div>
      <Table dataSource={brands} columns={columns} rowKey="id" loading={loading} pagination={false} />
      <Modal
        title={editingRecord ? 'Brendni tahrirlash' : 'Yangi brend'}
        open={isModalOpen}
        onCancel={() => setIsModalOpen(false)}
        footer={null}
        width={400}
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit} initialValues={{ isActive: true, sortOrder: 0 }}>
          <Form.Item name="name" label={t('carBrands.brandName')} rules={[{ required: true, message: 'Nomni kiriting' }]}>
            <Input placeholder="Chevrolet" />
          </Form.Item>
          <Form.Item name="sortOrder" label={t('carBrands.sortOrder')}>
            <InputNumber style={{ width: '100%' }} min={0} />
          </Form.Item>
          {editingRecord && (
            <Form.Item name="isActive" valuePropName="checked">
              <Switch checkedChildren={t('common.active')} unCheckedChildren={t('common.inactive')} />
            </Form.Item>
          )}
          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit" loading={saving}>
                {editingRecord ? 'Saqlash' : 'Yaratish'}
              </Button>
              <Button onClick={() => setIsModalOpen(false)}>{t('common.cancel')}</Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

// ============ MODELS TAB ============

const ModelsTab = () => {
  const [models, setModels] = useState<CarModel[]>([]);
  const [brands, setBrands] = useState<CarBrand[]>([]);
  const [loading, setLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingRecord, setEditingRecord] = useState<CarModel | null>(null);
  const [saving, setSaving] = useState(false);
  const [filterBrandId, setFilterBrandId] = useState<number | undefined>();
  const [form] = Form.useForm();

  const fetchData = async () => {
    setLoading(true);
    try {
      const brandsRes = await apiClient.get('/admin/car-brands');
      const brandsData: CarBrand[] = Array.isArray(brandsRes.data) ? brandsRes.data : brandsRes.data?.data || [];
      setBrands(brandsData);

      // Collect all models from brands
      const allModels: CarModel[] = [];
      for (const brand of brandsData) {
        for (const model of (brand.models || [])) {
          allModels.push({ ...model, brand: { id: brand.id, name: brand.name } });
        }
      }
      setModels(allModels);
    } catch {
      message.error('Ma\'lumotlarni yuklashda xatolik');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const handleCreate = () => {
    setEditingRecord(null);
    form.resetFields();
    form.setFieldsValue({ isActive: true, sortOrder: 0 });
    if (filterBrandId) form.setFieldsValue({ brandId: filterBrandId });
    setIsModalOpen(true);
  };

  const handleEdit = (record: CarModel) => {
    setEditingRecord(record);
    form.resetFields();
    setTimeout(() => form.setFieldsValue(record), 0);
    setIsModalOpen(true);
  };

  const handleDelete = async (id: number) => {
    try {
      await apiClient.delete(`/admin/car-models/${id}`);
      message.success('Model o\'chirildi');
      fetchData();
    } catch {
      message.error(t('common.error'));
    }
  };

  const handleSubmit = async (values: any) => {
    setSaving(true);
    try {
      if (editingRecord) {
        await apiClient.put(`/admin/car-models/${editingRecord.id}`, values);
        message.success('Model yangilandi');
      } else {
        await apiClient.post('/admin/car-models', values);
        message.success('Model yaratildi');
      }
      setIsModalOpen(false);
      fetchData();
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Xatolik yuz berdi');
    } finally {
      setSaving(false);
    }
  };

  const filteredModels = filterBrandId
    ? models.filter((m) => m.brandId === filterBrandId)
    : models;

  const columns = [
    { title: 'ID', dataIndex: 'id', key: 'id', width: 60 },
    { title: 'Nomi', dataIndex: 'name', key: 'name' },
    {
      title: 'Brend',
      key: 'brand',
      render: (_: unknown, record: CarModel) => record.brand?.name || '-',
    },
    {
      title: 'Tartib',
      dataIndex: 'sortOrder',
      key: 'sortOrder',
      width: 80,
    },
    {
      title: 'Holat',
      key: 'isActive',
      width: 100,
      render: (_: unknown, record: CarModel) => (
        <Tag color={record.isActive ? 'green' : 'default'}>
          {record.isActive ? t('common.active') : t('common.inactive')}
        </Tag>
      ),
    },
    {
      title: 'Amallar',
      key: 'actions',
      width: 100,
      render: (_: unknown, record: CarModel) => (
        <Space>
          <Button type="text" icon={<EditOutlined />} onClick={() => handleEdit(record)} />
          <Popconfirm title="O'chirishni tasdiqlaysizmi?" onConfirm={() => handleDelete(record.id)}>
            <Button type="text" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <>
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between' }}>
        <Select
          allowClear
          placeholder="Brendni tanlang"
          style={{ width: 250 }}
          value={filterBrandId}
          onChange={(val) => setFilterBrandId(val)}
          showSearch
          optionFilterProp="children"
        >
          {brands.map((b) => (
            <Select.Option key={b.id} value={b.id}>{b.name}</Select.Option>
          ))}
        </Select>
        <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate}>
          Yangi model
        </Button>
      </div>
      <Table dataSource={filteredModels} columns={columns} rowKey="id" loading={loading} pagination={false} />
      <Modal
        title={editingRecord ? 'Modelni tahrirlash' : 'Yangi model'}
        open={isModalOpen}
        onCancel={() => setIsModalOpen(false)}
        footer={null}
        width={400}
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit} initialValues={{ isActive: true, sortOrder: 0 }}>
          <Form.Item name="brandId" label="Brend" rules={[{ required: true, message: 'Brendni tanlang' }]}>
            <Select placeholder="Brendni tanlang" showSearch optionFilterProp="children">
              {brands.map((b) => (
                <Select.Option key={b.id} value={b.id}>{b.name}</Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item name="name" label={t('carBrands.modelName')} rules={[{ required: true, message: 'Nomni kiriting' }]}>
            <Input placeholder="Lacetti" />
          </Form.Item>
          <Form.Item name="sortOrder" label={t('carBrands.sortOrder')}>
            <InputNumber style={{ width: '100%' }} min={0} />
          </Form.Item>
          {editingRecord && (
            <Form.Item name="isActive" valuePropName="checked">
              <Switch checkedChildren={t('common.active')} unCheckedChildren={t('common.inactive')} />
            </Form.Item>
          )}
          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit" loading={saving}>
                {editingRecord ? 'Saqlash' : 'Yaratish'}
              </Button>
              <Button onClick={() => setIsModalOpen(false)}>Bekor qilish</Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

// ============ MAIN PAGE ============

export const CarBrandsPage = () => {
  const tabItems = [
    {
      key: 'brands',
      label: (
        <span>
          <CarOutlined /> {t('carBrands.brandsTab')}
        </span>
      ),
      children: <BrandsTab />,
    },
    {
      key: 'models',
      label: (
        <span>
          <CarOutlined /> {t('carBrands.modelsTab')}
        </span>
      ),
      children: <ModelsTab />,
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <h2 style={{ marginBottom: 24 }}>
        <CarOutlined style={{ marginRight: 8 }} />
        {t('carBrands.title')}
      </h2>
      <Card>
        <Tabs items={tabItems} defaultActiveKey="brands" />
      </Card>
    </div>
  );
};
